import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJsApiLoader } from '@react-google-maps/api';
import { useApi } from '../services/api';
import { formatWeekOptionLabel, weekNumberForDate, formatFullDate } from '../utils/forSale';
import { GOOGLE_MAPS_LIBRARIES } from '../utils/googleMaps';

const ALL_WEEKS = Array.from({ length: 52 }, (_, i) => i + 1);
const WEEK_YEAR = 2026;
const MAX_CHILD_AGE = 17;
const DESTINATION_DEBOUNCE_MS = 300;
const MAX_DESTINATION_RESULTS = 5;
const MAX_RESORT_RESULTS = 5;

function useOutsideClose(open, onClose) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    function handleKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);
  return ref;
}

function Stepper({ label, sublabel, value, onChange, min = 0, max = 16 }) {
  return (
    <div className="guest-stepper-row">
      <div>
        <div className="guest-stepper-label">{label}</div>
        {sublabel && <div className="guest-stepper-sublabel">{sublabel}</div>}
      </div>
      <div className="guest-stepper-controls">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label={`Decrease ${label}`}>−</button>
        <span>{value}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label={`Increase ${label}`}>+</button>
      </div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s7-6.5 7-11.5A7 7 0 105 9.5C5 14.5 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// `compact`: used on the mobile search-results page, where the full
// three-segment pill is too tall to sit above the results list. Renders a
// single collapsed row showing just the selected values until tapped, at
// which point it expands in place to the normal pill so the user can edit.
export default function SearchBar({ initialValues = {}, compact = false }) {
  const navigate = useNavigate();
  const { data } = useApi('/properties');
  const properties = useMemo(() => data?.properties || [], [data]);

  const { isLoaded: placesLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  // google.maps.places.AutocompleteService is the legacy API and is no longer
  // enabled for newer Cloud projects/keys — google.maps.places.AutocompleteSuggestion
  // (the Places API (New) client) is the supported replacement.
  const placesLibraryRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const [destinationResults, setDestinationResults] = useState([]);

  useEffect(() => {
    if (!placesLoaded || !window.google?.maps?.importLibrary) return;
    let cancelled = false;
    window.google.maps.importLibrary('places').then(lib => {
      if (cancelled) return;
      placesLibraryRef.current = lib;
      sessionTokenRef.current = new lib.AutocompleteSessionToken();
    });
    return () => { cancelled = true; };
  }, [placesLoaded]);

  function refreshSessionToken() {
    if (placesLibraryRef.current) {
      sessionTokenRef.current = new placesLibraryRef.current.AutocompleteSessionToken();
    }
  }

  const [openSegment, setOpenSegment] = useState(null); // 'where' | 'when' | 'guests' | null
  const closeSegment = useCallback(() => setOpenSegment(null), []);
  const containerRef = useOutsideClose(openSegment !== null, closeSegment);

  const [expanded, setExpanded] = useState(!compact);

  const [whereText, setWhereText] = useState(initialValues.q || '');
  const [weekFrom, setWeekFrom] = useState(initialValues.weekFrom || '');
  const [weekTo, setWeekTo] = useState(initialValues.weekTo || '');
  const [specificDate, setSpecificDate] = useState(initialValues.date || '');
  const [adults, setAdults] = useState(initialValues.adults ? Number(initialValues.adults) : 1);
  const [children, setChildren] = useState(initialValues.children ? Number(initialValues.children) : 0);
  const [childAges, setChildAges] = useState(() => {
    if (initialValues.ages) return initialValues.ages.split(',').map(a => Number(a) || 0);
    return [];
  });

  useEffect(() => {
    setChildAges(prev => {
      const next = prev.slice(0, children);
      while (next.length < children) next.push(10);
      return next;
    });
  }, [children]);

  const resortResults = useMemo(() => {
    const q = whereText.trim().toLowerCase();
    if (q.length < 1) return [];
    return properties
      .filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.location?.toLowerCase().includes(q) ||
        p.address?.toLowerCase().includes(q)
      )
      .slice(0, MAX_RESORT_RESULTS);
  }, [whereText, properties]);

  // Destinations come from the Google Places Autocomplete API, restricted to
  // regions/localities (cities, states, etc.) rather than businesses — those
  // live in the "Resorts" section above via the DB match instead.
  useEffect(() => {
    const term = whereText.trim();
    if (!term || !placesLibraryRef.current) {
      setDestinationResults([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      const { AutocompleteSuggestion } = placesLibraryRef.current;
      AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: term,
        includedPrimaryTypes: ['(regions)'],
        sessionToken: sessionTokenRef.current,
      }).then(({ suggestions }) => {
        if (cancelled) return;
        const mapped = (suggestions || [])
          .filter(s => s.placePrediction)
          .map(s => ({
            placeId: s.placePrediction.placeId,
            mainText: s.placePrediction.mainText?.text || s.placePrediction.text?.text,
            secondaryText: s.placePrediction.secondaryText?.text || '',
          }))
          .slice(0, MAX_DESTINATION_RESULTS);
        setDestinationResults(mapped);
      }).catch(() => {
        if (!cancelled) setDestinationResults([]);
      });
    }, DESTINATION_DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [whereText, placesLoaded]);

  function handleWhereChange(value) {
    setWhereText(value);
  }

  function pickDestination(destination) {
    const term = destination.mainText;
    setWhereText(term);
    setDestinationResults([]);
    closeSegment();
    refreshSessionToken();
    runSearch({ term });
  }

  function pickResort(property) {
    setWhereText(property.name);
    closeSegment();
    navigate(`/properties/${property.slug || property.id}`);
  }

  function setSpecificDateValue(value) {
    setSpecificDate(value);
    if (!value) return;
    const { week } = weekNumberForDate(value);
    const clamped = String(Math.min(52, Math.max(1, week)));
    setWeekFrom(clamped);
    setWeekTo(clamped);
  }

  function setWeekFromValue(value) {
    setWeekFrom(value);
    setSpecificDate('');
    const fromNum = Number(value) || 0;
    const toNum = Number(weekTo) || 0;
    if (value && toNum && toNum < fromNum) setWeekTo(value);
  }

  function clearWhen() {
    setWeekFrom(''); setWeekTo(''); setSpecificDate('');
  }

  const whenLabel = useMemo(() => {
    if (specificDate) return formatFullDate(specificDate);
    if (weekFrom && weekTo && weekFrom !== weekTo) return `Week ${weekFrom} – ${weekTo}`;
    if (weekFrom) return `Week ${weekFrom}`;
    return 'Any week';
  }, [weekFrom, weekTo, specificDate]);

  const guestsLabel = useMemo(() => {
    const total = adults + children;
    return `${total} guest${total !== 1 ? 's' : ''}`;
  }, [adults, children]);

  // `term` overrides whereText for callers (e.g. picking a destination) that
  // need to search with a value before the whereText state update has
  // actually committed — React state updates from the same handler aren't
  // visible yet when this runs.
  function runSearch({ term } = {}) {
    const searchTerm = term !== undefined ? term : whereText;
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set('q', searchTerm.trim());
    if (weekFrom) params.set('weekFrom', weekFrom);
    if (weekTo) params.set('weekTo', weekTo || weekFrom);
    if (specificDate) params.set('date', specificDate);
    params.set('adults', String(adults));
    if (children > 0) {
      params.set('children', String(children));
      params.set('ages', childAges.join(','));
    }
    closeSegment();
    navigate(`/search?${params.toString()}`);
    if (compact) setExpanded(false);
  }

  function handleSearch(e) {
    e?.preventDefault();
    runSearch();
  }

  const toggle = (seg) => setOpenSegment(s => (s === seg ? null : seg));

  if (compact && !expanded) {
    return (
      <button type="button" className="search-bar-compact" onClick={() => setExpanded(true)}>
        <span className="search-bar-compact-icon"><SearchIcon /></span>
        <span className="search-bar-compact-text">
          <span className="search-bar-compact-primary">{whereText || 'Anywhere'}</span>
          <span className="search-bar-compact-secondary">{whenLabel} · {guestsLabel}</span>
        </span>
      </button>
    );
  }

  return (
    <div className="search-bar" ref={containerRef}>
      {compact && (
        <button type="button" className="search-bar-collapse" onClick={() => setExpanded(false)} aria-label="Collapse search bar">
          <XIcon />
        </button>
      )}
      <div className="search-bar-pill">
        <div className="search-bar-segment">
          <button type="button" className={`search-bar-field ${openSegment === 'where' ? 'active' : ''}`} onClick={() => toggle('where')}>
            <span className="search-bar-field-label">Where</span>
            <span className="search-bar-field-value">{whereText || 'Search resorts, cities, states'}</span>
          </button>
          {openSegment === 'where' && (
            <div className="search-popover search-popover-where">
              <input
                type="text"
                autoFocus
                value={whereText}
                onChange={e => handleWhereChange(e.target.value)}
                placeholder="Resort name, city, or state…"
                className="search-popover-input"
              />
              {(destinationResults.length > 0 || resortResults.length > 0) && (
                <div className="search-where-results">
                  {destinationResults.length > 0 && (
                    <div className="search-where-section">
                      <div className="search-where-section-title">Destinations</div>
                      {destinationResults.map(dest => (
                        <div key={dest.placeId} className="search-where-row" onClick={() => pickDestination(dest)}>
                          <span className="search-where-row-icon"><PinIcon /></span>
                          <div className="search-where-row-text">
                            <div className="search-where-row-main">{dest.mainText}</div>
                            <div className="search-where-row-sub">{dest.secondaryText}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {resortResults.length > 0 && (
                    <div className="search-where-section">
                      <div className="search-where-section-title">Resorts</div>
                      {resortResults.map(p => (
                        <div key={p.id} className="search-where-row" onClick={() => pickResort(p)}>
                          <span className="search-where-row-thumb">
                            {p.image_urls?.[0] ? <img src={p.image_urls[0]} alt="" /> : <span className="search-where-row-thumb-fallback" />}
                          </span>
                          <div className="search-where-row-text">
                            <div className="search-where-row-main">{p.name}</div>
                            <div className="search-where-row-sub">{p.location || p.address}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="search-bar-divider" />

        <div className="search-bar-segment">
          <button type="button" className={`search-bar-field ${openSegment === 'when' ? 'active' : ''}`} onClick={() => toggle('when')}>
            <span className="search-bar-field-label">When</span>
            <span className="search-bar-field-value">{whenLabel}</span>
          </button>
          {openSegment === 'when' && (
            <div className="search-popover search-popover-when">
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <div style={{ flex: 1 }}>
                  <div className="search-popover-subtitle">From week</div>
                  <select value={weekFrom} onChange={e => setWeekFromValue(e.target.value)}>
                    <option value="">Any</option>
                    {ALL_WEEKS.map(w => <option key={w} value={w}>{formatWeekOptionLabel(w, WEEK_YEAR)}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="search-popover-subtitle">To week</div>
                  <select value={weekTo} onChange={e => { setWeekTo(e.target.value); setSpecificDate(''); }}>
                    <option value="">Any</option>
                    {ALL_WEEKS.filter(w => w >= (Number(weekFrom) || 1)).map(w => <option key={w} value={w}>{formatWeekOptionLabel(w, WEEK_YEAR)}</option>)}
                  </select>
                </div>
              </div>

              <div className="search-popover-divider" />

              <div className="search-popover-subtitle">Looking for a specific date?</div>
              <input type="date" value={specificDate} onChange={e => setSpecificDateValue(e.target.value)} />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                <button type="button" className="search-popover-clear" onClick={clearWhen}>Clear</button>
                <button type="button" className="search-popover-done" onClick={closeSegment}>Done</button>
              </div>
            </div>
          )}
        </div>

        <div className="search-bar-divider" />

        <div className="search-bar-segment">
          <button type="button" className={`search-bar-field ${openSegment === 'guests' ? 'active' : ''}`} onClick={() => toggle('guests')}>
            <span className="search-bar-field-label">Guests</span>
            <span className="search-bar-field-value">{guestsLabel}</span>
          </button>
          {openSegment === 'guests' && (
            <div className="search-popover search-popover-guests">
              <Stepper label="Adults" sublabel="Ages 18+" value={adults} onChange={setAdults} min={1} max={16} />
              <Stepper label="Children" sublabel="Ages 0–17" value={children} onChange={setChildren} min={0} max={8} />
              {children > 0 && (
                <div className="child-ages-list">
                  {childAges.map((age, i) => (
                    <div key={i} className="child-age-row">
                      <span>Child {i + 1} age</span>
                      <select
                        value={age}
                        onChange={e => setChildAges(ages => ages.map((a, idx) => idx === i ? Number(e.target.value) : a))}
                      >
                        {Array.from({ length: MAX_CHILD_AGE + 1 }, (_, n) => n).map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button type="button" className="search-popover-done" onClick={closeSegment}>Done</button>
              </div>
            </div>
          )}
        </div>

        <button type="button" className="search-bar-submit" onClick={handleSearch}>
          <SearchIcon />
          <span className="search-bar-submit-label">Search</span>
        </button>
      </div>
    </div>
  );
}
